import {
  ApiHolder,
  configApiRef,
  identityApiRef,
} from '@backstage/core-plugin-api';
import { FieldValidation } from '@rjsf/utils';
import { parseDynamicFields, validateUiSchema } from './CustomUrlUtilities';
import { fetchMicroservice } from '../utils/microserviceProxy';

/**
 * This method performs a validation on the selected values of the Custom URL Picker. The validation
 * is performed by calling the /validate API of the microservice.
 * @param selectedValues
 * @param validation
 * @param context
 * @param propSchema
 * @param formContext
 * @param fieldName
 * @returns
 */
export const customUrlPickerValidation = async (
  selectedValues: any,
  _validation: FieldValidation,
  context: { apiHolder: ApiHolder },
  propSchema: any,
  formContext: any,
  fieldName?: string,
) => {
  try {
    const configurations = context.apiHolder.get(configApiRef);
    const validatedSchema = validateUiSchema(propSchema);
    const apiSpec = validatedSchema['ui:apiSpec'];

    const allowArbitraryValues =
      (validatedSchema['ui:options']?.allowArbitraryValues as boolean) ?? false;

    const maxNumberOfItems = validatedSchema['ui:options']
      ?.maxNumberToSelect as number | undefined;

    // Use the effective validation spec (falls back to retrieval, same as fetchMicroservice)
    const validationSpec = apiSpec.validation ?? apiSpec.retrieval;

    // currently no validation for internal microservices
    if (
      !selectedValues ||
      (selectedValues && !selectedValues.length) ||
      validationSpec?.baseUrl === '{{CURRENT_HOST}}'
    ) {
      return {
        [fieldName!]: {
          __errors: [],
        },
      };
    }

    if (
      maxNumberOfItems &&
      selectedValues &&
      selectedValues.length > maxNumberOfItems
    ) {
      return {
        [fieldName!]: {
          __errors: [
            `It seems that the number of items that you have insert for this field exceed the maximum number enabled: ${maxNumberOfItems}. Please remove some values or the limit.`,
          ],
        },
      };
    }

    if (allowArbitraryValues) {
      return {
        [fieldName!]: {
          __error: [],
        },
      };
    }

    // the same params as retrieval are passed for validation as per spec, excluding limit
    const { limit, ...restParams } = apiSpec.retrieval?.params ?? {};

    const backendUrl =
      configurations?.getOptionalString('backend.baseUrl') || '';
    const identityApi = context.apiHolder.get(identityApiRef);
    const credentials = await identityApi?.getCredentials();
    const token = credentials?.token;

    const { status, data: responseBody } = await fetchMicroservice({
      backendBaseUrl: backendUrl,
      operation: 'validation',
      apiSpec,
      body: {
        selectedObjects: selectedValues,
        queryParameters: parseDynamicFields(
          formContext,
          null,
          () => {},
          () => {},
          () => {},
          () => {},
          restParams,
        ),
      },
      token,
    });

    if (status === 400) {
      const errors = responseBody?.errors?.map((err: any) => err.error) ?? [];

      return {
        [fieldName!]: {
          __errors: errors,
        },
      };
    }

    if (status === 500) {
      const errorList = responseBody?.errors ?? [];

      return {
        [fieldName!]: {
          __errors: [
            'It seems that the Microservice you are reaching is down or has thrown some exceptions. Please contact the platform team and whitens the field if you want to go on. Errors:',
            ...errorList,
          ],
        },
      };
    }

    if (status === 200) {
      return {
        [fieldName!]: {
          __errors: [],
        },
      };
    }

    return {
      [fieldName!]: {
        __errors: [
          'Error while validating the values selected. Please contact the platform team and whitens the field if you want to go on.',
        ],
      },
    };
  } catch (error) {
    if (error.message !== 'Network request failed') {
      return {
        [fieldName!]: {
          __errors: [error.message],
        },
      };
    }
    return {
      [fieldName!]: {
        __errors: [
          'It seems that the Microservice you are contacting is down, please contact the platform team and whitens the field if you want to go on.',
        ],
      },
    };
  }
};
